import { ScatterChart } from "@mantine/charts";
import { Skeleton, Stack, Title } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconSquareRotatedFilled } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { TransactionsTable } from "../TransactionsTable";
import { CarTransactionsTooltip } from "./components";
import { getDataAndDomain } from "./utils";

import { EmptyState } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { Currency } from "@/types/currency";
import { formatCurrency } from "@/utils/formatEuro";

import type { AuctionCarTransaction } from "@/types/auctions";
import type { FC } from "react";

export type CarTransactionsProps = {
  loading?: boolean;
  marketCarId?: string;
  namespace?: string;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (query: string) => void;
  page?: number;
  pageSize?: number;
  searchValue?: string;
  totalTransactions?: number;
  transactions?: AuctionCarTransaction[];
};

export const CarTransactions: FC<CarTransactionsProps> = ({
  loading = false,
  marketCarId,
  namespace,
  onPageChange,
  onPageSizeChange,
  onSearch,
  page,
  pageSize,
  searchValue,
  totalTransactions,
  transactions = [],
}) => {
  const { t } = useTranslation();

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const { data, domain } = getDataAndDomain(transactions, currency);
  const isSearching = Boolean(searchValue?.trim());
  const hasChartData = data.length > 0;

  return (
    <Stack>
      <Title order={3}>{t("components.CarTransactions.title")}</Title>
      <Skeleton visible={loading}>
        <Stack>
          {hasChartData ? (
            <ScatterChart
              h={300}
              data={data}
              dataKey={{ x: "date", y: "value" }}
              strokeDasharray="0"
              tickLine="none"
              xAxisProps={{
                domain,
              }}
              scatterProps={{ shape: <IconSquareRotatedFilled size={8} /> }}
              valueFormatter={{
                x: (value) => dayjs.unix(value).format("MMM YYYY"),
                y: (value) =>
                  formatCurrency(value, currency.code, {
                    notation: "compact",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  }),
              }}
              tooltipProps={{
                content: ({ payload }) => {
                  return <CarTransactionsTooltip payload={payload ? [...payload] : undefined} />;
                },
                cursor: { strokeDasharray: "5 5" },
              }}
            />
          ) : (
            <EmptyState
              type={isSearching ? "noResults" : "noData"}
              text={
                isSearching
                  ? t("components.TransactionsTable.no_results", {
                      defaultValue: t("common.noData"),
                    })
                  : t("common.noData")
              }
              h={300}
            />
          )}
          <TransactionsTable
            columns={["date", "type", "status", "price", "seller", "event", "more"]}
            marketCarId={marketCarId}
            namespace={namespace}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSearch={onSearch}
            page={page}
            pageSize={pageSize}
            searchValue={searchValue}
            suppressEmptyState={!hasChartData}
            totalTransactions={totalTransactions}
            transactions={transactions}
          />
        </Stack>
      </Skeleton>
    </Stack>
  );
};
